import React, { useState } from "react";

import {
  faArrowDown,
  faArrowUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import {
  Button,
  Card,
  Tooltip,
  Typography,
} from "@samskip/frontend-components";
import { format } from "date-fns";
import { navigate } from "gatsby";
import { TFunction, useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import {
  QuoteStatus,
  UserRole,
  QuoteStatusActions,
  NavigationConstants,
} from "@assets/constants";
import { useAppDispatch } from "@assets/hooks/redux";
import {
  FileResponse,
  ListQuoteVM,
  QuoteDecline,
  QuoteVM,
  UpdateQuoteRequest,
} from "@assets/types";
import { FileUtilities, translate } from "@assets/utilities";
import { classNames } from "@assets/utilities/classNameUtilities";
import { numberCommaDecimal } from "@assets/utilities/stringUtilities";
import {
  QuoteApprovalModal,
  QuoteDeclineModal,
  ExpandableList,
  TransportRoute,
  QuoteListItem,
} from "@components";
import QuoteStatusTag from "@components/Tag/quoteStatus";
import QuoteService from "@services/QuoteService";
import UserService from "@services/UserService";
import { getQuotes } from "@state/slices/quotesSlice";

interface QuoteCardProps {
  quote: ListQuoteVM;
}

const hasFinanceAccess = () => {
  return UserService.hasUserAccessToRoles([UserRole.ADMIN, UserRole.FINANCE]);
};

const bookQuote = async (quoteReference: string) => {
  return window.open(
    `${process.env.MainServiceWeb}/shipments/create/${quoteReference}/book-quote`
  );
};

const downloadQuoteFile = async (quoteReference: string) => {
  await QuoteService.getQuoteFile(quoteReference)
    .then((response: FileResponse) => {
      FileUtilities.downloadFile(
        response.FileName,
        response.ContentType,
        response.Content
      );
    })
    .catch(() => {
      toast.error(translate("ERROR_QUOTE_FILE_NOT_FOUND"));
    });
};

const openQuoteFile = async (quoteReference: string) => {
  await QuoteService.getQuoteFile(quoteReference)
    .then((response: FileResponse) => {
      FileUtilities.openFile(response.ContentType, response.Content);
    })
    .catch(() => {
      toast.error(translate("ERROR_QUOTE_FILE_NOT_FOUND"));
    });
};

const renderViewQuoteLinks = (t: TFunction, quoteReference: string) => (
  <div className="flex flex-col md:flex-row gap-4 h-fit">
    <Button
      iconLeft={faArrowUpRightFromSquare}
      onClick={() => openQuoteFile(quoteReference)}
      variant="text"
    >
      {t("LABEL_VIEW_QUOTE")}
    </Button>
    <Button
      iconLeft={faArrowDown}
      onClick={() => downloadQuoteFile(quoteReference)}
      variant="text"
    >
      {t("LABEL_DOWNLOAD_QUOTE")}
    </Button>
  </div>
);

const QuoteCard = ({ quote }: QuoteCardProps) => {
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const updateQuoteStatus = async (
    quoteReference: string,
    quoteAction: QuoteStatusActions,
    quoteDecline?: QuoteDecline
  ) => {
    const requestObject: UpdateQuoteRequest = {
      QuoteReference: quoteReference,
      QuoteStatus: quoteAction,
      QuoteDecline: quoteDecline,
    };
    try {
      await QuoteService.updateQuoteStatus(requestObject);
      dispatch(getQuotes());
      switch (quoteAction) {
        case QuoteStatusActions.Approve:
          toast.success(translate("LABEL_QUOTE_APPROVED"));
          break;
        case QuoteStatusActions.Decline:
          toast.success(translate("LABEL_QUOTE_DECLINED"));
          break;
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const declineQuote = (quoteReference: string, quoteDecline: QuoteDecline) => {
    updateQuoteStatus(quoteReference, QuoteStatusActions.Decline, quoteDecline);
  };

  const approveQuote = (quoteReference: string) => {
    updateQuoteStatus(quoteReference, QuoteStatusActions.Approve);
  };

  const requestQuoteUpdate = (quoteReference: string) => {
    setLoadingSpinner(true);
    QuoteService.getQuoteEditVM(quoteReference)
      .then((quoteVM: QuoteVM) => {
        navigate(
          `${NavigationConstants.NAVIGATION_LINKS.quotes}/requestUpdate`,
          {
            state: { quote: quoteVM },
          }
        );
      })
      .catch(() => {
        // Ignore error
      })
      .finally(() => setLoadingSpinner(false));
  };

  const getCardFooter = () => {
    switch (quote.Status) {
      case QuoteStatus.Pending:
        return (
          <ExpandableList
            closedText={t("LABEL_SHOW_MORE")}
            elementTransform={(note) => (
              <QuoteListItem
                description={note.Message}
                header={format(new Date(note.Date), "dd MMM yyyy, HH:mm")}
                key={`${quote.QuoteReference}-${note.Id}`}
              />
            )}
            expandedText={t("LABEL_SHOW_LESS")}
            list={quote.Notes}
            max={3}
          />
        );

      case QuoteStatus.ReadyForApproval:
        return (
          hasFinanceAccess() && (
            <>
              {renderViewQuoteLinks(t, quote.QuoteReference)}
              <div className="flex gap-4">
                <QuoteDeclineModal
                  declineQuote={declineQuote}
                  quoteReference={quote.QuoteReference}
                />
                <QuoteApprovalModal
                  approveQuote={approveQuote}
                  quoteReference={quote.QuoteReference}
                />
              </div>
            </>
          )
        );

      case QuoteStatus.Valid:
        return (
          <>
            {hasFinanceAccess() &&
              renderViewQuoteLinks(t, quote.QuoteReference)}
            <Tooltip
              content={t("LABEL_BOOK_DISABLED")}
              hidden={quote.Shipment.length <= 1}
            >
              <Button
                disabled={quote.Shipment.length > 1}
                onClick={() => bookQuote(quote.QuoteReference)}
              >
                {t("LABEL_BOOK")}
              </Button>
            </Tooltip>
          </>
        );

      case QuoteStatus.Expired:
        return (
          hasFinanceAccess() && (
            <>
              {renderViewQuoteLinks(t, quote.QuoteReference)}
              <Tooltip
                content={t("LABEL_REQUEST_DISABLED")}
                hidden={quote.Shipment.length <= 1}
              >
                <Button
                  disabled={quote.Shipment.length > 1}
                  loading={loadingSpinner}
                  onClick={() => requestQuoteUpdate(quote.QuoteReference)}
                >
                  {t("LABEL_REQUEST_UPDATE")}
                </Button>
              </Tooltip>
            </>
          )
        );
      default:
        return null;
    }
  };

  return (
    <Card bordered className="border-neutral-300 rounded-lg p-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-4">
        {/* Main */}
        <div
          className={`md:col-span-4 
            bg-neutral-100 p-4 
            flex flex-col gap-2
            rounded-lg
            `}
        >
          <Typography
            color="neutral-900"
            fontWeight="semibold"
            variant="body-base"
          >
            {quote.QuoteReference}
          </Typography>
          <div className="flex flex-col">
            {quote.Shipment.map((sh, index) => (
              <Tooltip
                content={sh.ShipmentPackaging}
                key={`${quote.QuoteReference}-${index}`}
              >
                <p className="text-neutral-1000 overflow-x-hidden whitespace-nowrap text-ellipsis">
                  {quote.ShipmentType} • {sh.ShipmentQuantity} x{" "}
                  {sh.ShipmentPackaging}
                </p>
              </Tooltip>
            ))}
          </div>
          <QuoteStatusTag quoteStatus={quote.Status} />
        </div>
        {/* End Main */}
        {/* Route */}
        <div
          className="md:col-span-4
            md:border-r md:border-neutral-300 md:py-4 md: pr-4
            flex flex-col gap-2"
        >
          <Typography fontWeight="semibold" textCase="upper" variant="label">
            {t("LABEL_ROUTE")}
          </Typography>
          <TransportRoute
            fromPlace={quote.FromAddress.City}
            fromTransportType={quote.FromAddress.Type}
            toPlace={quote.ToAddress.City}
            toTransportType={quote.ToAddress.Type}
          />
          <p>
            {t("LABEL_INCOTERMS")}: {quote.IncoTerms}
          </p>
        </div>
        {/* End Route */}
        {/* Validity */}
        <div
          className="md:col-span-2 
            md:border-r md:border-neutral-300 md:py-4
            flex flex-col gap-2"
        >
          <Typography fontWeight="semibold" textCase="upper" variant="label">
            {t("LABEL_VALID_UNTIL")}
          </Typography>
          <p className="text-neutral-1000">
            {quote.Status === QuoteStatus.Pending &&
              t("LABEL_QUOTE_IN_PROGRESS")}
            {quote.Status !== QuoteStatus.Pending &&
              quote.EndDate &&
              format(new Date(quote.EndDate), "dd.MM.yyyy")}
          </p>
        </div>
        {/* End Validity */}
        {/* Price */}
        <div
          className="md:col-span-2 md:py-4
          flex flex-col gap-2"
        >
          <Typography fontWeight="semibold" textCase="upper" variant="label">
            {t("LABEL_PRICE")}
          </Typography>
          {hasFinanceAccess() ? (
            <p className="text-neutral-1000">
              {quote.Status === QuoteStatus.Pending
                ? t("LABEL_QUOTE_IN_PROGRESS")
                : quote.Price
                  ? `${numberCommaDecimal(quote.Price)} ${quote.PriceUnit}`
                  : ""}
            </p>
          ) : (
            <p className="text-neutral-1000" />
          )}
        </div>
        {/* End Price */}
      </div>
      <div
        className={classNames(
          "flex flex-col md:flex-row gap-6 md:gap-2 md:items-center",
          hasFinanceAccess() ? "justify-between" : "justify-end",
          (quote.Status !== QuoteStatus.Pending || quote.Notes.length > 0) &&
            "border-t border-neutral-300 rounded-b-xl mt-4 pt-4"
        )}
      >
        {getCardFooter()}
      </div>
    </Card>
  );
};

export default QuoteCard;
